import { useState, useCallback } from 'react';
import { useEditorState } from '../contexts';

type UseFileCacheParams = {
  initialFiles: Record<string, string>;
  initialWorking: Record<string, string>;
};

export function useFileCache({ initialFiles, initialWorking }: UseFileCacheParams) {
  const { setOpenTabs } = useEditorState();

  const [persistedFiles, setPersistedFiles] = useState<Record<string, string>>(initialFiles);
  const [workingFiles, setWorkingFiles] = useState<Record<string, string>>(initialWorking);
  const [fetchedFiles, setFetchedFiles] = useState<Record<string, boolean>>({});
  const [cacheVersion, setCacheVersion] = useState(0);

  const handleTreeDeleteCleanup = useCallback((removedIds: string[]) => {
    removedIds.forEach((id) => {
      setPersistedFiles((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      setWorkingFiles((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      setOpenTabs((prev) => prev.filter((tab) => tab !== id));
    });
  }, [setOpenTabs]);

  const handleTreeMoveCleanup = useCallback((pathChanges: Record<string, string>) => {
    // Update persisted files with new paths
    setPersistedFiles((prev) => {
      const next: Record<string, string> = {};
      Object.entries(prev).forEach(([path, content]) => {
        const newPath = pathChanges[path];
        if (newPath) {
          next[newPath] = content;
        } else {
          next[path] = content;
        }
      });
      return next;
    });

    // Update working files with new paths
    setWorkingFiles((prev) => {
      const next: Record<string, string> = {};
      Object.entries(prev).forEach(([path, content]) => {
        const newPath = pathChanges[path];
        if (newPath) {
          next[newPath] = content;
        } else {
          next[path] = content;
        }
      });
      return next;
    });

    // Update open tabs with new paths
    setOpenTabs((prev) => prev.map((tab) => pathChanges[tab] || tab));

    // Update fetched files with new paths
    setFetchedFiles((prev) => {
      const next: Record<string, boolean> = {};
      Object.entries(prev).forEach(([path, value]) => {
        const newPath = pathChanges[path];
        if (newPath) {
          next[newPath] = value;
        } else {
          next[path] = value;
        }
      });
      return next;
    });
  }, [setOpenTabs]);

  return {
    persistedFiles,
    setPersistedFiles,
    workingFiles,
    setWorkingFiles,
    fetchedFiles,
    setFetchedFiles,
    cacheVersion,
    setCacheVersion,
    handleTreeDeleteCleanup,
    handleTreeMoveCleanup,
  };
}
